import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, UserPlus, CreditCard, HandCoins, CheckCheck } from 'lucide-react';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

const TYPE_META = {
    lead: { icon: UserPlus, cls: 'bg-blue-500/15 text-blue-400' },
    payment: { icon: CreditCard, cls: 'bg-emerald-500/15 text-emerald-400' },
    donation: { icon: HandCoins, cls: 'bg-rose-500/15 text-rose-400' },
};

function timeAgo(iso) {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'à l\'instant';
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return `il y a ${Math.floor(diff / 86400)} j`;
}

export default function SuperAdminNotifications({ items, unread, markAllRead }) {
    const navigate = useNavigate();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    data-testid="sa-notif-bell"
                    className="relative h-9 w-9 rounded-md flex items-center justify-center text-stone-300 hover:text-white hover:bg-white/5 transition-colors"
                    aria-label="Notifications"
                >
                    <Bell size={17} strokeWidth={1.6} />
                    {unread > 0 && (
                        <span
                            data-testid="sa-notif-badge"
                            className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-amber-500 text-stone-950 text-[10px] font-bold flex items-center justify-center"
                        >
                            {unread > 9 ? '9+' : unread}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-80" data-testid="sa-notif-panel">
                <div className="flex items-center justify-between pr-1">
                    <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                        Notifications
                    </DropdownMenuLabel>
                    {unread > 0 && (
                        <button
                            type="button"
                            onClick={markAllRead}
                            data-testid="sa-notif-mark-read"
                            className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                        >
                            <CheckCheck size={12} /> Tout marquer lu
                        </button>
                    )}
                </div>
                <DropdownMenuSeparator />
                {items.length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="sa-notif-empty">
                        Aucune activité récente
                    </div>
                )}
                <div className="max-h-80 overflow-y-auto">
                    {items.map((n) => {
                        const meta = TYPE_META[n.type] || TYPE_META.lead;
                        const Icon = meta.icon;
                        return (
                            <DropdownMenuItem
                                key={n.id}
                                onClick={() => navigate(n.link || '/app/super-admin')}
                                data-testid={`sa-notif-item-${n.type}`}
                                className="items-start gap-2.5 py-2.5 cursor-pointer"
                            >
                                <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${meta.cls}`}>
                                    <Icon size={15} strokeWidth={1.7} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm leading-snug truncate">{n.title}</div>
                                    {n.subtitle && <div className="text-xs text-muted-foreground truncate">{n.subtitle}</div>}
                                    <div className="text-[10px] text-muted-foreground/70 mt-0.5">{timeAgo(n.created_at)}</div>
                                </div>
                            </DropdownMenuItem>
                        );
                    })}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
